import * as React from "react";
import { fetchEmployeeIdByAuthUserId, fetchEmployeeScoreByEmployeeId, getCurrentUser } from "../api/queries";
import { Box, Card, Typography, Button, Stack, Divider } from "@mui/material";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useI18n } from "../i18n/I18nProvider";


export default function EmployeeScreen(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;
  
  const { lang } = useI18n();
  const L = React.useCallback((en: string, de: string) => (lang === "de" ? de : en), [lang]); // ✅ added

  const [score, setScore] = React.useState<number>(0);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [uiError, setUiError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let mounted = true;

    async function loadScore() {
      setLoading(true);
      setUiError(null);

      try {
        // 1) Get logged-in user
        const { data: userData, error: userErr } = await getCurrentUser();
        if (userErr) throw userErr;
        if (!userData?.user) throw new Error(L("Not authenticated.", "Nicht authentifiziert."));

        const authUserId = userData.user.id;

        // 2) Map auth user -> employee id
        const { data: empRow, error: empErr } = await fetchEmployeeIdByAuthUserId(authUserId);

        if (empErr) throw empErr;
        if (!empRow?.id) throw new Error(L("Employee record not found for this account.", "Kein Mitarbeiterdatensatz für dieses Konto gefunden."));

        // 3) Fetch current kontrol_score from employee_scores (single row per employee)
        const { data: scoreRow, error: scoreErr } = await fetchEmployeeScoreByEmployeeId(empRow.id);

        if (scoreErr) throw scoreErr;

        const nextScore =
          typeof scoreRow?.kontrol_score === "number"
            ? scoreRow.kontrol_score
            : Number(scoreRow?.kontrol_score ?? 0) || 0;

        if (!mounted) return;
        setScore(nextScore);
      } catch (e: any) {
        if (!mounted) return;
        setUiError(e?.message ?? L("Failed to load the score.", "Score konnte nicht geladen werden."));
        setScore(0);
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    }

    loadScore();

    return () => {
      mounted = false;
    };
  }, [L]);

  const meta = scoreLabel(score, lang);

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 56px)",
        display: "grid",
        placeItems: "center",
        p: { xs: 2, sm: 3 },
        bgcolor: "#fff",
        overflowX: "hidden",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 980, mx: "auto" }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            p: { xs: 2, sm: 3 },
            border: "1px solid #0000000f",
            background: `
              linear-gradient(
                90deg,
                #eaecf4 0%,
                #e0e3ef 55%,
                #eeeff6 100%
              )
            `,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            sx={{ minWidth: 0 }}
          >
            {/* Left: title + explanation */}
            <Stack spacing={1} sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1.2} alignItems="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2.5,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "#4e75da24",
                    border: "1px solid #5c7fff38",
                    flexShrink: 0,
                  }}
                >
                  <VerifiedUserOutlinedIcon sx={{ color: "#111" }} />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 1000,
                      fontSize: 28,
                      lineHeight: 1.1,
                      color: "#111",
                    }}
                  >
                    {L("Your Readiness Level", "Dein Bereitschaftsgrad")}
                  </Typography>
                  <Typography sx={{ color: "#0000009e", fontSize: 13 }}>
                    {L(
                      "Snapshot of your current operational availability.",
                      "Momentaufnahme deiner aktuellen Einsatzverfügbarkeit."
                    )}
                  </Typography>
                </Box>
              </Stack>
            </Stack>

            {/* Right: score block */}
            <Box
              sx={{
                borderRadius: 3,
                p: 2,
                minWidth: { xs: "100%", md: 320 },
                border: "1px solid #0000000f",
                bgcolor: "#ffffff8c",
              }}
            >
              <Typography sx={{ fontWeight: 1000, color: "#000000b3", fontSize: 13 }}>
                {L("Current score", "Aktueller Score")}
              </Typography>

              <Stack direction="row" spacing={1.2} alignItems="baseline" sx={{ mt: 0.2 }}>
                <Typography
                  sx={{
                    fontWeight: 1100,
                    fontSize: 54,
                    lineHeight: 1,
                    color: meta.color,
                    opacity: loading ? 0.65 : 1,
                  }}
                >
                  {loading ? "…" : score}
                </Typography>
                <Typography sx={{ fontWeight: 1000, fontSize: 18, color: "#0000008c" }}>/ 5</Typography>
              </Stack>

              <Typography sx={{ fontWeight: 1000, color: meta.color, mt: 0.5 }}>
                {meta.label}
              </Typography>

              <Typography sx={{ color: "#0000009e", fontSize: 12.5, mt: 1 }}>
                {L(
                  "Your score helps tailor the next steps in your action plan.",
                  "Dein Score hilft dabei, die nächsten Schritte in deinem Aktionsplan passend zu empfehlen."
                )}
              </Typography>

              {uiError && (
                <Typography sx={{ color: "#b91c1c", fontWeight: 900, fontSize: 12.5, mt: 1 }}>
                  {uiError}
                </Typography>
              )}
            </Box>
          </Stack>

          <Divider sx={{ my: 2.4, borderColor: "#0000001a" }} />

          {/* Bottom: CTA row */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.2fr 0.8fr" },
              gap: 2,
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                borderRadius: 3,
                p: 1.6,
                border: "1px solid #0000000f",
                bgcolor: "#ffffff73",
              }}
            >
              <Typography sx={{ fontWeight: 1000, color: "#111" }}>
                {L("What happens next?", "Wie geht es weiter?")}
              </Typography>
              <Typography sx={{ color: "#000000a6", fontSize: 13, mt: 0.4 }}>
                {L(
                  "Your action plan suggests modules to improve readiness and reduce personal risk. You can start modules anytime and track progress of your readiness level.",
                  "Dein Aktionsplan schlägt Module vor, um die Vorbereitung zu verbessern und dein persönliches Risiko zu reduzieren. Du kannst jederzeit starten und den Fortschritt deines Bereitschaftsgrads verfolgen."
                )}
              </Typography>
            </Box>

            <Stack spacing={1}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => handlePageChange("ActionPlan")}
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{
                  borderRadius: 2.6,
                  textTransform: "none",
                  fontWeight: 1000,
                  bgcolor: "#2a2b3b",
                  boxShadow: "none",
                  py: 1.1,
                  "&:hover": { bgcolor: "#22242f", boxShadow: "none" },
                }}
              >
                {L("View your action plan", "Aktionsplan ansehen")}
              </Button>

              <Button
                fullWidth
                variant="outlined"
                onClick={() => handlePageChange("KontrolScore")}
                sx={{
                  borderRadius: 2.6,
                  textTransform: "none",
                  fontWeight: 1000,
                  borderColor: "#00000047",
                  color: "#111",
                  py: 1.1,
                  "&:hover": {
                    borderColor: "#00000073",
                    bgcolor: "#5c77ff0f",
                  },
                }}
              >
                {L("Check my readiness level", "Check mein Bereitschaftsgrad")}
              </Button>
            </Stack>
          </Box>
        </Card>
      </Box>
    </Box>
  );
}

function scoreLabel(score: number, lang: "en" | "de"): { label: string; color: string } {
  if (score >= 4) return { label: lang === "de" ? "Hohe Vorbereitung" : "High readiness", color: "#166534" };
  if (score >= 2) return { label: lang === "de" ? "Mittlere Vorbereitung" : "Moderate readiness", color: "#92400e" };
  return { label: lang === "de" ? "Niedrige Vorbereitung" : "Low readiness", color: "#b91c1c" };
}
